import { hubBody, hubRequest } from "./client";
import type {
  Task,
  TaskComment,
  TaskActivity,
  SavedView,
  Project,
  Milestone,
  ProjectUpdate,
  CalendarItem,
} from "./types";
export const listTasks = (includeTriage = false) =>
  hubRequest<Task[]>(
    "/tasks",
    {},
    includeTriage ? { includeTriage: "true" } : undefined,
  );
export const getTask = (id: string) =>
  hubRequest<Task>(`/tasks/${encodeURIComponent(id)}`);
export const createTask = (task: Partial<Task>) =>
  hubRequest<Task>("/tasks", hubBody(task));
export type TaskPatch = Partial<Task> & {
  expectedUpdatedAt?: number;
  expectedVersion?: number;
};
export const updateTask = (id: string, patch: TaskPatch) =>
  hubRequest<Task>(`/tasks/${encodeURIComponent(id)}`, hubBody(patch, "PATCH"));
export type BatchTaskResult = {
  id: string;
  success: boolean;
  task?: Task;
  error?: string;
};
export const batchTasks = (ids: string[], patch: Partial<Task>) =>
  hubRequest<BatchTaskResult[]>("/tasks/batch", hubBody({ ids, patch }));
export const listComments = (id: string) =>
  hubRequest<TaskComment[]>(`/tasks/${encodeURIComponent(id)}/comments`);
export const addComment = (id: string, content: string, mentionIds: string[]) =>
  hubRequest<TaskComment>(
    `/tasks/${encodeURIComponent(id)}/comments`,
    hubBody({ content, mentionIds }),
  );
export const listActivity = (id: string) =>
  hubRequest<TaskActivity[]>(`/tasks/${encodeURIComponent(id)}/activity`);
export const listViews = () => hubRequest<SavedView[]>("/views");
export const saveView = (
  name: string,
  filters: Record<string, string>,
  layout: string,
) => hubRequest<SavedView>("/views", hubBody({ name, filters, layout }));
export const listProjects = () => hubRequest<Project[]>("/projects");
export const getProject = (id: string) =>
  hubRequest<Project>(`/projects/${encodeURIComponent(id)}`);
export const createProject = (project: Partial<Project>) =>
  hubRequest<Project>("/projects", hubBody(project));
export const updateProject = (id: string, patch: Partial<Project>) =>
  hubRequest<Project>(
    `/projects/${encodeURIComponent(id)}`,
    hubBody(patch, "PATCH"),
  );
export const adoptProject = (tasklistGuid: string, title: string) =>
  hubRequest<Project>("/projects/adopt", hubBody({ tasklistGuid, title }));
export const addMilestone = (id: string, milestone: Partial<Milestone>) =>
  hubRequest<Milestone>(
    `/projects/${encodeURIComponent(id)}/milestones`,
    hubBody(milestone),
  );
export const addProjectUpdate = (
  id: string,
  content: string,
  health: Project["health"],
  updateId?: string,
) =>
  hubRequest<ProjectUpdate>(
    `/projects/${encodeURIComponent(id)}/updates`,
    hubBody({ content, health, updateId }),
  );
export const listCalendar = (from: string, to: string, personId?: string) =>
  hubRequest<CalendarItem[]>("/calendar", {}, { from, to, personId });
export const rescheduleCalendar = (id: string, date: string) =>
  hubRequest<CalendarItem>(
    `/calendar/${encodeURIComponent(id)}`,
    hubBody({ date }, "PATCH"),
  );

export type WorkPending = {
  id: string;
  kind: string;
  entityId: string;
  status: "pending" | "sending" | "awaiting_auth" | "needs_review" | "rejected";
  attempts: number;
  error?: string;
  hasRemoteReceipt?: boolean;
  canCompensate?: boolean;
  manualDisposition?: {
    kind: "parked_unknown";
    active: boolean;
    reason: string;
    actorId: string;
    createdAt: number;
  };
};
export const listWorkPending = () => hubRequest<WorkPending[]>("/work/pending");
export const retryWorkPending = (id: string, remoteAbsent = false) =>
  hubRequest(
    `/work/pending/${encodeURIComponent(id)}/retry`,
    hubBody({ remoteAbsent }),
  );
export const dismissWorkPending = (id: string) =>
  hubRequest(`/work/pending/${encodeURIComponent(id)}/dismiss`, hubBody({}));
export const compensateWorkPending = (id: string) =>
  hubRequest(`/work/pending/${encodeURIComponent(id)}/compensate`, hubBody({}));

export const verifyWorkPending = (id: string, remoteGuid?: string) =>
  hubRequest(
    `/work/pending/${encodeURIComponent(id)}/verify`,
    hubBody({ remoteGuid }),
  );

export const deleteMilestone = (projectId: string, milestoneId: string) =>
  hubRequest<{ id: string; deleted: boolean }>(
    `/projects/${encodeURIComponent(projectId)}/milestones/${encodeURIComponent(milestoneId)}`,
    { method: "DELETE" },
  );

export type WorkSyncError = {
  projectId?: string;
  customerId?: string;
  taskGuid?: string;
  kind?: string;
  status: number | string;
  message: string;
  at?: number;
};
export const listWorkSyncErrors = () =>
  hubRequest<WorkSyncError[]>("/work/sync-errors");
export type WorkReceiptCandidate = {
  guid: string;
  title: string;
  kind: string;
  createdAt?: number;
  sourceUrl?: string;
};
export const listWorkReceiptCandidates = (id: string) =>
  hubRequest<{ candidates: WorkReceiptCandidate[]; guidance?: string }>(
    `/work/pending/${encodeURIComponent(id)}/candidates`,
  );

export const parkWorkPending = (id: string, reason: string) =>
  hubRequest(
    `/work/pending/${encodeURIComponent(id)}/park`,
    hubBody({ reason }),
  );

export type ActionDecision = {
  id: string;
  kind: string;
  question: string;
  options: { id: string; label: string; action?: unknown }[];
  recommendation: string;
  evidence: string;
  deciderIds: string[];
  submitterId?: string;
  submitterName?: string;
  submitterKeyId?: string;
  projectId?: string;
  responsibilityId?: string;
  entityType?: string;
  entityId?: string;
  dueDate?: string | null;
  blocking?: boolean;
  status: "open" | "resolving" | "needs_review" | "resolved" | "withdrawn";
  execution?: {
    state: "prepared" | "executing" | "applied" | "needs_review" | "committed";
    optionId: string;
    actorId?: string;
    error?: string | null;
    recoverable: boolean;
    executionId?: string;
    logId?: string;
    request?: {
      optionId: string;
      conclusion: string;
      evidence: string | null;
      assigneeId?: string;
      assigneeType?: "person" | "agent";
    };
  } | null;
  result?: unknown;
  version: number;
  priority?: number;
  createdAt?: number;
  impact?: string;
  consequence?: string;
  sourceUrl?: string;
};
export const listActionDecisions = () =>
  hubRequest<{ items: ActionDecision[] }>(
    "/action-decisions",
    {},
    { status: "open", mine: "1" },
  ).then((r) => r.items);
export const resolveActionDecision = (
  id: string,
  optionId: string,
  expectedVersion: number,
  reassignment?: { assigneeId: string; assigneeType: "person" | "agent" },
) =>
  hubRequest<ActionDecision>(
    `/action-decisions/${encodeURIComponent(id)}/resolve`,
    hubBody({ optionId, version: expectedVersion, ...reassignment }),
  );
/** 恢复只复用服务端持久化的原请求，不采用页面仍留着的输入。 */
export const resumeActionDecision = (decision: ActionDecision) => {
  const execution = decision.execution,
    fixed = execution?.request;
  if (
    !execution?.recoverable ||
    !fixed ||
    fixed.optionId !== execution.optionId
  )
    return Promise.reject(
      new Error("缺少可安全恢复的原决定，请先核对执行记录"),
    );
  return hubRequest<ActionDecision>(
    `/action-decisions/${encodeURIComponent(decision.id)}/resolve`,
    hubBody({
      version: decision.version,
      optionId: fixed.optionId,
      conclusion: fixed.conclusion,
      evidence: fixed.evidence,
      ...(fixed.assigneeId === undefined
        ? {}
        : {
            assigneeId: fixed.assigneeId,
            assigneeType: fixed.assigneeType ?? "person",
          }),
    }),
  );
};

export type WorkMetrics = {
  month?: string;
  aiCompleted?: number;
  totalCompleted?: number;
  aiRatio?: number;
  unsyncedCount?: number;
};
export const getWorkMetrics = () => hubRequest<WorkMetrics>("/work/metrics");
export const assignTask = (
  id: string,
  assigneeId: string,
  assigneeType: "person" | "agent",
  version?: number,
) =>
  hubRequest<Task>(
    `/tasks/${encodeURIComponent(id)}/assign`,
    hubBody({ assigneeId, assigneeType, version }),
  );
